import React from "react"
import { useTranslation } from "react-i18next"
import Animate, { AnimationTypes } from "../Animate/Animate"
import ModalPortal from "../Modal/ModalPortal"
import defaultStyle from "../../styles/defaultPageStyle.module.css"

type PricingBenefitsPopupProps = {
  onClose: () => void
}

type BenefitGroup = {
  accentClassName: string
  icon: React.ReactNode
  items: string[]
  titleKey: string
}

const benefitGroups: BenefitGroup[] = [
  {
    titleKey: "pricing.popup.groups.time_clock.title",
    items: [
      "pricing.popup.groups.time_clock.items.punch",
      "pricing.popup.groups.time_clock.items.geofence",
      "pricing.popup.groups.time_clock.items.validations",
      "pricing.popup.groups.time_clock.items.schedules",
      "pricing.popup.groups.time_clock.items.adjustments",
      "pricing.popup.groups.time_clock.items.live",
      "pricing.popup.groups.time_clock.items.reports",
      "pricing.popup.groups.time_clock.items.labor_cost",
    ],
    accentClassName: "from-sky-500 to-cyan-500",
    icon: (
      <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth="2"
          d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
        />
      </svg>
    ),
  },
  {
    titleKey: "pricing.popup.groups.checklists.title",
    items: [
      "pricing.popup.groups.checklists.items.templates",
      "pricing.popup.groups.checklists.items.steps",
      "pricing.popup.groups.checklists.items.execution",
      "pricing.popup.groups.checklists.items.overdue_alert",
      "pricing.popup.groups.checklists.items.history",
    ],
    accentClassName: "from-emerald-500 to-teal-500",
    icon: (
      <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth="2"
          d="M9 12l2 2 4-4m5 2a9 9 0 11-18 0 9 9 0 0118 0z"
        />
      </svg>
    ),
  },
  {
    titleKey: "pricing.popup.groups.operations.title",
    items: [
      "pricing.popup.groups.operations.items.orders",
      "pricing.popup.groups.operations.items.command",
      "pricing.popup.groups.operations.items.fulfillment",
      "pricing.popup.groups.operations.items.servicePoints",
      "pricing.popup.groups.operations.items.reservations",
      "pricing.popup.groups.operations.items.external_orders",
      "pricing.popup.groups.operations.items.print_agent",
      "pricing.popup.groups.operations.items.multi_payment_provider",
      "pricing.popup.groups.operations.items.tap_to_pay",
      "pricing.popup.groups.operations.items.system_modules",
    ],
    accentClassName: "from-red-500 to-orange-500",
    icon: (
      <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth="2"
          d="M9 5H7a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2"
        />
      </svg>
    ),
  },
  {
    titleKey: "pricing.popup.groups.self_service.title",
    items: [
      "pricing.popup.groups.self_service.items.mobile_ordering",
      "pricing.popup.groups.self_service.items.customer_account",
      "pricing.popup.groups.self_service.items.realtime_order",
      "pricing.popup.groups.self_service.items.mobile_payment",
      "pricing.popup.groups.self_service.items.service_point_self_service",
      "pricing.popup.groups.self_service.items.service_point_tokens",
      "pricing.popup.groups.self_service.items.device_orders",
      "pricing.popup.groups.self_service.items.customer_check",
    ],
    accentClassName: "from-cyan-500 to-blue-500",
    icon: (
      <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth="2"
          d="M7 4h10a2 2 0 012 2v12a2 2 0 01-2 2H7a2 2 0 01-2-2V6a2 2 0 012-2zm3 13h4"
        />
      </svg>
    ),
  },
  {
    titleKey: "pricing.popup.groups.catalog.title",
    items: [
      "pricing.popup.groups.catalog.items.public_catalog",
      "pricing.popup.groups.catalog.items.products",
      "pricing.popup.groups.catalog.items.combos",
      "pricing.popup.groups.catalog.items.categories",
      "pricing.popup.groups.catalog.items.category_order",
      "pricing.popup.groups.catalog.items.additionals",
      "pricing.popup.groups.catalog.items.selections",
      "pricing.popup.groups.catalog.items.reservations",
      "pricing.popup.groups.catalog.items.excel_products",
      "pricing.popup.groups.catalog.items.data_import",
      "pricing.popup.groups.catalog.items.quick_edit",
    ],
    accentClassName: "from-amber-500 to-orange-500",
    icon: (
      <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 6h16M4 12h16M4 18h10" />
      </svg>
    ),
  },
  {
    titleKey: "pricing.popup.groups.sales.title",
    items: [
      "pricing.popup.groups.sales.items.qr_code",
      "pricing.popup.groups.sales.items.social",
      "pricing.popup.groups.sales.items.discovery",
      "pricing.popup.groups.sales.items.public_menu_customization",
      "pricing.popup.groups.sales.items.promotions",
      "pricing.popup.groups.sales.items.coupons",
      "pricing.popup.groups.sales.items.loyalty",
      "pricing.popup.groups.sales.items.extra_services",
      "pricing.popup.groups.sales.items.notices",
      "pricing.popup.groups.sales.items.tracking",
    ],
    accentClassName: "from-orange-500 to-amber-400",
    icon: (
      <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth="2"
          d="M11 3a1 1 0 011 1v1.07a7.002 7.002 0 015.93 5.93H19a1 1 0 110 2h-1.07a7.002 7.002 0 01-5.93 5.93V20a1 1 0 11-2 0v-1.07A7.002 7.002 0 014.07 13H3a1 1 0 110-2h1.07A7.002 7.002 0 0110 5.07V4a1 1 0 011-1zm0 4a5 5 0 100 10 5 5 0 000-10z"
        />
      </svg>
    ),
  },
  {
    titleKey: "pricing.popup.groups.finance.title",
    items: [
      "pricing.popup.groups.finance.items.daily_summary",
      "pricing.popup.groups.finance.items.period_metrics",
      "pricing.popup.groups.finance.items.cashflow",
      "pricing.popup.groups.finance.items.channel_metrics",
      "pricing.popup.groups.finance.items.external_consolidation",
      "pricing.popup.groups.finance.items.employee_metrics",
      "pricing.popup.groups.finance.items.payroll",
      "pricing.popup.groups.finance.items.catalog_metrics",
      "pricing.popup.groups.finance.items.online_payments",
      "pricing.popup.groups.finance.items.reconciliation",
    ],
    accentClassName: "from-emerald-500 to-teal-500",
    icon: (
      <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth="2"
          d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8v2m0 12v2m8-8a8 8 0 11-16 0 8 8 0 0116 0z"
        />
      </svg>
    ),
  },
  {
    titleKey: "pricing.popup.groups.fiscal.title",
    items: [
      "pricing.popup.groups.fiscal.items.integration",
      "pricing.popup.groups.fiscal.items.issuer",
      "pricing.popup.groups.fiscal.items.product_taxation",
      "pricing.popup.groups.fiscal.items.automatic_emission",
      "pricing.popup.groups.fiscal.items.manual_emission",
      "pricing.popup.groups.fiscal.items.documents",
      "pricing.popup.groups.fiscal.items.cancellation",
      "pricing.popup.groups.fiscal.items.purchase_imports",
    ],
    accentClassName: "from-red-800 to-rose-600",
    icon: (
      <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth="2"
          d="M7 3h10a1 1 0 011 1v16l-3-2-2 2-2-2-2 2-3-2V4a1 1 0 011-1zm2 5h6M9 12h6M9 16h3"
        />
      </svg>
    ),
  },
  {
    titleKey: "pricing.popup.groups.delivery.title",
    items: [
      "pricing.popup.groups.delivery.items.addresses",
      "pricing.popup.groups.delivery.items.pickup_addresses",
      "pricing.popup.groups.delivery.items.shipping",
      "pricing.popup.groups.delivery.items.geolocation",
      "pricing.popup.groups.delivery.items.couriers",
      "pricing.popup.groups.delivery.items.dispatch",
      "pricing.popup.groups.delivery.items.routing",
      "pricing.popup.groups.delivery.items.notifications",
      "pricing.popup.groups.delivery.items.courier_financial",
    ],
    accentClassName: "from-lime-500 to-emerald-500",
    icon: (
      <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth="2"
          d="M9 17h6m-6-4h6m5 0h-2l-1-2H7L6 13H4m0 0v3a1 1 0 001 1h1a1 1 0 001-1v-1h10v1a1 1 0 001 1h1a1 1 0 001-1v-3M4 13l1.5-5h13L20 13"
        />
      </svg>
    ),
  },
  {
    titleKey: "pricing.popup.groups.settings.title",
    items: [
      "pricing.popup.groups.settings.items.company_data",
      "pricing.popup.groups.settings.items.social_media",
      "pricing.popup.groups.settings.items.public_link",
      "pricing.popup.groups.settings.items.service_area",
      "pricing.popup.groups.settings.items.public_menu_customization",
      "pricing.popup.groups.settings.items.schedules",
      "pricing.popup.groups.settings.items.vacation",
      "pricing.popup.groups.settings.items.fulfillment_settings",
      "pricing.popup.groups.settings.items.employees",
      "pricing.popup.groups.settings.items.permissions",
      "pricing.popup.groups.settings.items.store_groups",
    ],
    accentClassName: "from-sky-500 to-indigo-500",
    icon: (
      <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth="2"
          d="M12 6V4m0 16v-2m6-6h2M4 12h2m10.24-4.24 1.42-1.42M6.34 17.66l1.42-1.42m8.48 0 1.42 1.42M6.34 6.34l1.42 1.42M12 15a3 3 0 100-6 3 3 0 000 6z"
        />
      </svg>
    ),
  },
  {
    titleKey: "pricing.popup.groups.whatsapp.title",
    items: [
      "pricing.popup.groups.whatsapp.items.whatsapp_agent",
      "pricing.popup.groups.whatsapp.items.order_guidance",
      "pricing.popup.groups.whatsapp.items.whatsapp_ai",
      "pricing.popup.groups.whatsapp.items.store_context",
      "pricing.popup.groups.whatsapp.items.whatsapp_crm",
      "pricing.popup.groups.whatsapp.items.human_handoff",
      "pricing.popup.groups.whatsapp.items.automatic_messages",
      "pricing.popup.groups.whatsapp.items.whatsapp_official",
      "pricing.popup.groups.whatsapp.items.resposta_ai",
    ],
    accentClassName: "from-emerald-600 to-cyan-500",
    icon: (
      <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth="2"
          d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-3 3v-3z"
        />
      </svg>
    ),
  },
  {
    titleKey: "pricing.popup.groups.intelligence.title",
    items: [
      "pricing.popup.groups.intelligence.items.ai_metrics",
      "pricing.popup.groups.intelligence.items.business_context",
      "pricing.popup.groups.intelligence.items.operational_agents",
      "pricing.popup.groups.intelligence.items.cloud_ai",
      "pricing.popup.groups.intelligence.items.pwa_notifications",
      "pricing.popup.groups.intelligence.items.platform_notices",
      "pricing.popup.groups.intelligence.items.operational_alerts",
      "pricing.popup.groups.intelligence.items.pwa",
    ],
    accentClassName: "from-violet-600 to-fuchsia-500",
    icon: (
      <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth="2"
          d="M12 3l1.4 4.1L18 8.5l-4.6 1.4L12 14l-1.4-4.1L6 8.5l4.6-1.4L12 3zm6 11 .8 2.2L21 17l-2.2.8L18 20l-.8-2.2L15 17l2.2-.8L18 14z"
        />
      </svg>
    ),
  },
  {
    titleKey: "pricing.popup.groups.support.title",
    items: [
      "pricing.popup.groups.support.items.help_center",
      "pricing.popup.groups.support.items.tutorials",
      "pricing.popup.groups.support.items.quick_search",
      "pricing.popup.groups.support.items.owner_onboarding",
      "pricing.popup.groups.support.items.receipts",
      "pricing.popup.groups.support.items.multilanguage",
      "pricing.popup.groups.support.items.security",
      "pricing.popup.groups.support.items.plan_management",
    ],
    accentClassName: "from-slate-600 to-slate-800",
    icon: (
      <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth="2"
          d="M12 18h.01M9.1 9a3 3 0 115.8 1c-.5 1.5-2.9 1.6-2.9 3M12 22a10 10 0 100-20 10 10 0 000 20z"
        />
      </svg>
    ),
  },
]

const PricingBenefitsPopup: React.FC<PricingBenefitsPopupProps> = ({ onClose }) => {
  const { t } = useTranslation("login")

  return (
    <ModalPortal>
      <div className={defaultStyle.overlay}>
        <div className={defaultStyle.alert2}>
          <Animate type={AnimationTypes.fadeInTraslate} duration="0.25s">
            <div className="relative w-full max-h-[90vh] overflow-y-auto rounded-lg border-2 border-white/10 bg-white shadow-2xl">
              <div className="relative bg-gradient-to-r from-amber-700 via-orange-500 to-yellow-400 px-6 py-6 text-white md:px-8">
                <div className="pointer-events-none absolute inset-0 bg-black/30" />

                <button
                  onClick={onClose}
                  className="absolute right-4 top-4 z-30 flex h-10 w-10 items-center justify-center rounded-full bg-black/25 text-lg font-black text-white transition-all duration-200 hover:bg-black/35 active:scale-95"
                  aria-label={t("pricing.popup.close")}
                  data-interaction-description-key="interactionDescriptions:components.landPage.pricingBenefitsPopup.button.close"
                >
                  ×
                </button>

                <div className="relative z-10">
                  <p className="mb-2 inline-flex rounded-full bg-black/20 px-3 py-1 text-[11px] font-extrabold uppercase tracking-[0.18em] text-amber-50">
                    {t("pricing.popup.badge")}
                  </p>

                  <h3 className="max-w-3xl text-2xl font-black leading-tight text-white drop-shadow-[0_2px_8px_rgba(0,0,0,0.45)] md:text-4xl">
                    {t("pricing.popup.title")}
                  </h3>

                  <p className="mt-3 max-w-3xl text-sm text-white drop-shadow-[0_1px_5px_rgba(0,0,0,0.35)] md:text-base">
                    {t("pricing.popup.subtitle")}
                  </p>
                </div>
              </div>

              <div className="min-h-0 flex-1 overflow-y-auto overflow-x-hidden bg-slate-50 px-3 py-4 md:px-8 md:py-8">
                <div className="grid grid-cols-1 gap-4 lg:grid-cols-2 xl:grid-cols-3">
                  {benefitGroups.map((group) => (
                    <Animate key={group.titleKey} type={AnimationTypes.fadeInTraslate} duration="0.35s">
                      <section className="h-full min-w-0 rounded-xl border border-slate-200 bg-white p-3 shadow-sm md:p-4">
                        <div className="mb-4 flex items-center gap-3">
                          <div
                            className={`flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-gradient-to-br ${group.accentClassName} text-white shadow-lg`}
                          >
                            {group.icon}
                          </div>

                          <div className="min-w-0">
                            <p className="text-xs font-extrabold uppercase tracking-[0.16em] text-orange-600">
                              {t("pricing.popup.groupLabel")}
                            </p>
                            <h4 className="break-words text-lg font-black text-slate-900">{t(group.titleKey)}</h4>
                          </div>
                        </div>

                        <ul className="space-y-3">
                          {group.items.map((itemKey) => (
                            <li key={itemKey} className="flex items-start gap-3 rounded-xl bg-slate-50 px-3 py-3">
                              <span className="mt-1 inline-flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-emerald-100 text-emerald-600">
                                <svg className="h-3.5 w-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth="3"
                                    d="M5 13l4 4L19 7"
                                  />
                                </svg>
                              </span>

                              <span className="min-w-0 break-words text-sm font-medium leading-relaxed text-slate-700">
                                {t(itemKey)}
                              </span>
                            </li>
                          ))}
                        </ul>
                      </section>
                    </Animate>
                  ))}
                </div>
              </div>
            </div>
          </Animate>
        </div>
      </div>
    </ModalPortal>
  )
}

export default PricingBenefitsPopup
